import type { PermissionContext } from "@swot/swot-components";
import type { AuthRepository } from "./authRepository.js";
import { buildPermissionContext } from "./permissionBridge.js";
import type { CmtsStoredSession } from "./authStore.js";
import { mergeCmtsSession } from "./authStore.js";
import type { CmtsBackendMenuItem } from "@store/permissionSlice.js";
import type { CmtsOrgContext, CmtsTenantState } from "@store/tenantSlice.js";
import type { CmtsUserContext } from "../view-models/complaintViewModels.js";
import { normalizeCmtsRoles } from "./normalizeCmtsRoles.js";

export type CmtsAuthBootstrapStatus = "idle" | "restoring" | "authenticated" | "unauthenticated" | "failed";

export type CmtsNormalizedOrganizationScope = {
  readonly organizationUnitIds: readonly string[];
  readonly primaryOrganizationUnitId?: string | undefined;
  readonly orgContext?: CmtsOrgContext | undefined;
  readonly raw: readonly unknown[];
};

export type CmtsAuthBootstrapSnapshot = {
  readonly session: CmtsStoredSession;
  readonly user: CmtsUserContext;
  readonly permissions: readonly string[];
  readonly roles: readonly string[];
  readonly menu: readonly CmtsBackendMenuItem[];
  readonly organizationScope: CmtsNormalizedOrganizationScope;
  readonly tenant: Partial<CmtsTenantState>;
  readonly permissionContext: PermissionContext;
};

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function readString(value: unknown, keys: readonly string[], fallback = ""): string {
  const record = readRecord(value);
  for (const key of keys) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (typeof candidate === "number" || typeof candidate === "boolean") return String(candidate);
  }
  return fallback;
}

function normalizeStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.map((item) => {
    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") return String(item).trim();
    return readString(item, ["code", "Code", "permissionCode", "PermissionCode", "name", "Name", "roleCode", "RoleCode"], "");
  }).filter(Boolean))).sort();
}

function normalizeOrganizationScope(scope: readonly unknown[], user: CmtsUserContext, storedSession: CmtsStoredSession): CmtsNormalizedOrganizationScope {
  const userScopeIds = normalizeStringArray((user as unknown as Record<string, unknown>).organizationScopes ?? (user as unknown as Record<string, unknown>).OrganizationScopes)
    .map(String);
  const scopedIds = scope.map((item) => readString(item, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId", "id", "Id"], "")).filter(Boolean);
  const organizationUnitIds = Array.from(new Set([
    ...(storedSession.organizationUnitId ? [storedSession.organizationUnitId] : []),
    ...(storedSession.organizationUnitIds ?? []),
    ...userScopeIds,
    ...scopedIds,
  ].map(String).filter(Boolean)));
  const primaryOrganizationUnitId = organizationUnitIds[0];
  const firstScopeRecord = scope.map(readRecord).find((item) => readString(item, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId", "id", "Id"], "") === primaryOrganizationUnitId) ?? readRecord(scope[0]);
  const orgUnitCode = readString(firstScopeRecord, ["organizationUnitCode", "OrganizationUnitCode", "code", "Code"], "");
  const orgUnitName = readString(firstScopeRecord, ["organizationUnitName", "OrganizationUnitName", "name", "Name"], "");
  const siteId = readString(firstScopeRecord, ["siteId", "SiteId"], "");
  const siteCode = readString(firstScopeRecord, ["siteCode", "SiteCode"], "");
  const orgContext = primaryOrganizationUnitId ? {
    orgUnitId: primaryOrganizationUnitId,
    ...(orgUnitCode ? { orgUnitCode } : {}),
    ...(orgUnitName ? { orgUnitName } : {}),
    ...(siteId ? { siteId } : {}),
    ...(siteCode ? { siteCode } : {}),
  } : undefined;
  return { organizationUnitIds, primaryOrganizationUnitId, orgContext, raw: scope };
}

export async function bootstrapAuthenticatedSession(repository: AuthRepository, storedSession: CmtsStoredSession | null): Promise<CmtsAuthBootstrapSnapshot | null> {
  if (!storedSession?.accessToken) return null;
  const tenantId = storedSession.tenantId;
  const user = await repository.me(tenantId);
  const permissions = normalizeStringArray(await repository.myPermissions(tenantId));
  const menu = await repository.myMenu(tenantId);
  const rawScope = await repository.myOrganizationScope(tenantId);
  const organizationScope = normalizeOrganizationScope(rawScope, user, storedSession);
  const roles = normalizeCmtsRoles([...(user.roles ?? []), ...(storedSession.roles ?? []), ...(storedSession.roleIds ?? [])]).sort();
  const tenant = {
    tenantId: user.tenantContext.tenantId || storedSession.tenantId,
    tenantCode: storedSession.tenantCode,
    tenantName: user.tenantContext.tenantName || storedSession.tenantName,
    timezoneCode: user.tenantContext.timezoneCode ?? "Asia/Kolkata",
    orgContext: organizationScope.orgContext,
  } satisfies Partial<CmtsTenantState>;
  const sessionPatch: CmtsStoredSession = {
    ...storedSession,
    userId: user.userId || storedSession.userId,
    displayName: user.displayName || storedSession.displayName,
    email: user.email || storedSession.email,
    roles,
    roleIds: roles,
    permissions,
    permissionCodes: permissions,
    tenantId: tenant.tenantId,
    tenantCode: tenant.tenantCode,
    tenantName: tenant.tenantName,
    organizationUnitId: organizationScope.primaryOrganizationUnitId,
    organizationUnitIds: organizationScope.organizationUnitIds,
  };
  mergeCmtsSession(sessionPatch);
  return {
    session: sessionPatch,
    user,
    permissions,
    roles,
    menu,
    organizationScope,
    tenant,
    permissionContext: buildPermissionContext({
      userId: sessionPatch.userId,
      tenantId: sessionPatch.tenantId,
      roles,
      permissions,
      siteIds: sessionPatch.siteIds,
      confidentialityClearance: sessionPatch.confidentialityClearance?.toLowerCase?.() as PermissionContext["confidentialityClearance"] | undefined,
    }),
  };
}

export function isAuthBootstrapRestoring(status: CmtsAuthBootstrapStatus): boolean {
  return status === "idle" || status === "restoring";
}
